import Card from '../../components/card/Card';
import H from '../../components/texts/h/H';
import P from '../../components/texts/p/P';
import Table from '../../components/table/table';
import DragAndDrop from '../../components/DragAndDrop/DragAndDrop';
import DatePicker from '../../components/datepicker/DatePicker';
import { MAX_FILE_SIZE } from '../../utils/Constants';
import { FormValues } from '../../pages/MonetizationNew';
import { useState } from 'react';
import Span from '../../components/texts/span/Span';
import Icon from '../../components/icon/Icon';
import Label from '../../components/texts/label/Label';
import { 
  downloadLoanMonetizationFile, 
  deleteLoanMonetizationFile 
} from '../../services/MonetizationService';
import { getMimeType } from '../../utils/Utils';

interface LoanExecutionSectionProps {
  formValues: FormValues;
  updateFormValues: (updates: Partial<FormValues>) => void;
  installments: Array<{
    installment_number: number;
    due_date: string;
    amortization: number;
    interest: number;
    final_balance: number;
  }>;
  idGlobal?: string;
  isPublicOperation?: boolean;
}

const LoanExecutionSection: React.FC<LoanExecutionSectionProps> = ({
  formValues,
  updateFormValues,
  installments,
  idGlobal,
  isPublicOperation = false,
}) => {
  const [fileError, setFileError] = useState<string>('');

  const formatNumber = (amount: number | null | undefined) => {
    if (amount === null || amount === undefined || isNaN(amount)) {
      return '0';
    }
    if (Number.isInteger(amount)) {
      return amount.toLocaleString('es-AR');
    }
    return amount.toLocaleString('es-AR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-');
    return `${day}/${month}/${year}`;
  };

  const handleFileChange = (file: File) => {
    setFileError('');
    // Store the File object to be uploaded later when Save/Execute is clicked
    updateFormValues({ 
      pending_file: file,
      bank_contract_file: file.name 
    });
  };

  const handleFileError = (error: any) => {
    // Ensure we always set a string error, even if we receive an object
    if (typeof error === 'object' && error !== null) {
      if (error.message) {
        setFileError(error.message);
      } else {
        setFileError('Error al procesar el archivo');
      }
    } else if (typeof error === 'string') {
      setFileError(error);
    } else {
      setFileError('Error desconocido al procesar el archivo');
    }
  };

  const handleFileView = async () => {
    if (!idGlobal || !formValues.bank_contract_file) return;
    
    try {
      const response = await downloadLoanMonetizationFile(idGlobal);
      if (response.success && 'data' in response) {
        const mimeType = getMimeType(formValues.bank_contract_file);
        const fileBlob = new Blob([response.data], { type: mimeType });
        const url = window.URL.createObjectURL(fileBlob);
        
        if (mimeType.startsWith('application/pdf') || mimeType.startsWith('image/')) {
          window.open(url, '_blank');
        } else {
          const a = document.createElement('a');
          a.href = url;
          a.download = formValues.bank_contract_file;
          a.click();
          window.URL.revokeObjectURL(url);
        }
      }
    } catch (error) {
      console.error('Error viewing file:', error);
      setFileError('Error al visualizar el archivo');
    }
  };

  const handleFileDownload = async () => {
    if (!idGlobal || !formValues.bank_contract_file) return;
    
    try {
      const response = await downloadLoanMonetizationFile(idGlobal);
      if (response.success && 'data' in response) {
        const mimeType = getMimeType(formValues.bank_contract_file);
        const fileBlob = new Blob([response.data], { type: mimeType });
        const url = window.URL.createObjectURL(fileBlob);
        
        // Forzar descarga directa sin abrir en navegador
        const a = document.createElement('a');
        a.href = url;
        a.download = formValues.bank_contract_file;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      setFileError('Error al descargar el archivo');
    }
  };

  const handleFileDelete = async () => {
    // If there's a pending file (not uploaded yet), just clear it
    if (formValues.pending_file) {
      updateFormValues({ 
        pending_file: undefined,
        bank_contract_file: '' 
      });
      return;
    }

    // If file is already uploaded, delete from server
    if (!idGlobal || !formValues.bank_contract_file) return;

    try {
      const response = await deleteLoanMonetizationFile(idGlobal, formValues.bank_contract_file);
      if (response.success) {
        updateFormValues({ 
          pending_file: undefined,
          bank_contract_file: '' 
        });
      } else {
        setFileError('Error al eliminar el archivo');
      }
    } catch (error) {
      console.error('Error deleting file:', error);
      setFileError('Error al eliminar el archivo');
    }
  };

  const columns = [
    {
      name: 'Nº cuota',
      selector: (row: any) => (
        <Card>
          <P type='3' variant='neutral'>
            {row.installment_number}
          </P>
        </Card>
      ),
      width: '10%',
    },
    {
      name: 'F. vencimiento',
      selector: (row: any) => (
        <Card>
          <P type='3' variant='neutral'>
            {formatDate(row.due_date)}
          </P>
        </Card>
      ),
      width: '15%',
    },
    {
      name: 'Amortización',
      selector: (row: any) => (
        <Card>
          <P type='3' variant='neutral'>
            {formatNumber(row.amortization)}
          </P>
        </Card>
      ),
      width: '18%',
    },
    {
      name: 'Interés',
      selector: (row: any) => (
        <Card>
          <P type='3' variant='neutral'>
            {formatNumber(row.interest)}
          </P>
        </Card>
      ),
      width: '18%',
    },
    {
      name: 'Total cuota',
      selector: (row: any) => (
        <Card>
          <P type='3' variant='neutral'>
            {formatNumber((row.amortization || 0) + (row.interest || 0))}
          </P>
        </Card>
      ),
      width: '18%',
    },
    {
      name: 'Saldo final',
      selector: (row: any) => (
        <Card>
          <P type='3' variant='neutral'>
            {formatNumber(row.final_balance)}
          </P>
        </Card>
      ),
      width: '21%',
    },
  ];

  return (
    <Card margin={{ top: '4' }}>
      <Card spacing='4' borderRadiusAmount='3'>
        <H variant="secondary-2" type='4' weight='bold' modifier='strong'>
          Ejecución de aval
        </H>
      </Card>

      {/* Monetization Date */}
      <Card margin={{ top: '3' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
          <Card>
            <P withMargin={false} variant='neutral' type='4' modifier='strong'>
              Fecha de monetización
            </P>
            <Card margin={{ top: '2' }}>
              <DatePicker
                defaultValue={formValues.monetization_date ?? ''}
                onChange={(date: string) => updateFormValues({ monetization_date: date })}
                disabled={false}
                displayFormat="DD/MM/AA"
              />
            </Card>
          </Card>
        </div>
      </Card>

      {/* Installments Table */}
      {installments && installments.length > 0 && (
        <Card margin={{ top: '3' }}>
          <Table
            columns={columns}
            data={installments}
            tableData={{
              size: installments.length,
              page: 0,
              totalElements: installments.length,
              totalPages: 1,
            }}
            handleChangePage={() => {}}
            variant='normal'
          />
        </Card>
      )}

      {!isPublicOperation && (
        <Card margin={{ top: '4' }}>
          <Card margin={{ bottom: '2' }}>
            <P type='2' variant='neutral-1' weight='bold' modifier='strong' withMargin={false}>
              Contrato bancario por uso del aval
            </P>
          </Card>
          
          {!formValues.bank_contract_file && (
            <DragAndDrop
              name='bank_contract'
              setValue={(file) => handleFileChange(file)}
              fileTypes={['pdf', 'jpg', 'jpeg', 'png', 'xls', 'xlsx', 'doc', 'docx']}
              maxFileSize={MAX_FILE_SIZE}
              showError={(error: any) => handleFileError(error)}
            />
          )}
          
          {fileError && (
            <Card margin={{ top: '2' }}>
              <Span variant='danger' type='2'>
                {fileError}
              </Span>
            </Card>
          )}
          
          {formValues.bank_contract_file && (
            <Card margin={{ top: '2' }}>
              <Card
                display='flex'
                alignItems='center'
                gap='16px'
              >
                <Icon variant='neutral' name='document_outline' />
                <Card
                  display='flex'
                  justifyContent='space-between'
                  width='100%'
                  variant='neutral'
                  modifier='inverted'
                  alignItems='center'
                  spacing={{ left: '2', right: '2' }}
                  borderRadiusAmount='3'
                  border
                  contain='content'
                >
                  <Card>
                    <Label variant='neutral' modifier='strong' type='2'>
                      {formValues.bank_contract_file}
                    </Label>
                    {formValues.pending_file && (
                      <Card margin={{ top: '1' }}>
                        <Span variant='warning' type='3'>
                          Pendiente de subir (se subirá al guardar o ejecutar)
                        </Span>
                      </Card>
                    )}
                  </Card>
                  <Card display='flex' gap='10px'>
                    {!formValues.pending_file && (
                      <>
                        <Icon
                          name='view_outline'
                          variant='neutral'
                          height={44}
                          cursor='pointer'
                          onClick={handleFileView}
                        />
                        <Icon
                          name='download_outline'
                          variant='neutral'
                          height={44}
                          cursor='pointer'
                          onClick={handleFileDownload}
                        />
                      </>
                    )}
                    <Icon
                      name='delete_outline'
                      variant='neutral'
                      height={44}
                      cursor='pointer'
                      onClick={handleFileDelete}
                    />
                  </Card>
                </Card>
              </Card>
            </Card>
          )}
        </Card>
      )}
    </Card>
  );
};

export default LoanExecutionSection;
